import React, { useEffect, useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";

import {
  FaUser,
  FaHome,
  FaPills,
  FaPhone,
  FaShoppingCart,
  FaCheckCircle,
  FaFlask,
  FaBox,
  FaSignOutAlt,
  FaUpload,
  FaBars,
  FaTimes,
  FaCloudUploadAlt,
  FaClipboardList,
} from "react-icons/fa";

import CartSidebar from "./CartSidebar.jsx";
import { BASE_URL } from "../backend/backend.js";

export const navbarHeight = "4.5rem";

// =====================================================
// SCREEN SIZE HOOK
// =====================================================

const useScreenSize = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(
    window.innerWidth < breakpoint
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };

    window.addEventListener("resize", handleResize);

    return () =>
      window.removeEventListener("resize", handleResize);
  }, [breakpoint]);

  return isMobile;
};

// =====================================================
// ANIMATED LOGO
// =====================================================

const AnimatedLogo = ({ isMobileScreen }) => {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
      }}
    >
      <motion.div
        animate={{
          rotate: [0, 10, -10, 0],
        }}
        transition={{
          repeat: Infinity,
          duration: 4,
        }}
        style={{
          width: isMobileScreen ? "38px" : "45px",
          height: isMobileScreen ? "38px" : "45px",
          borderRadius: "14px",
          background: "linear-gradient(135deg,#2563eb,#7c3aed)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          fontWeight: "900",
          fontSize: isMobileScreen ? "0.9rem" : "1rem",
          boxShadow: "0 10px 20px rgba(37,99,235,0.3)",
        }}
      >
        GM
      </motion.div>

      <div>
        <div
          style={{
            fontSize: isMobileScreen ? "1rem" : "1.4rem",
            fontWeight: "800",
            background:
              "linear-gradient(135deg,#1e293b,#2563eb)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            lineHeight: 1,
          }}
        >
          Girraj Medicare
        </div>

        {!isMobileScreen && (
          <div
            style={{
              fontSize: "0.75rem",
              color: "#64748b",
              marginTop: "2px",
              fontWeight: "500",
            }}
          >
            Smart Healthcare Platform
          </div>
        )}
      </div>
    </div>
  );
};

// =====================================================
// USER PROFILE DROPDOWN
// =====================================================

const UserProfileDropdown = ({
  user,
  handleCartClick,
  performLogout,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] =
    useState(false);

  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleClickOutside
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, []);

  const isDoctor =
    user?.roles?.some(
      (role) => role.name === "DOCTOR"
    );

  const isUser =
    user?.roles?.some(
      (role) => role.name === "USER"
    );

  return (
    <div
      ref={dropdownRef}
      style={{
        position: "relative",
      }}
    >
      <motion.button
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.95 }}
        onClick={() =>
          setIsDropdownOpen(!isDropdownOpen)
        }
        style={{
          border: "none",
          background: "white",
          borderRadius: "40px",
          padding: "6px 14px 6px 6px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          cursor: "pointer",
          boxShadow: "0 5px 20px rgba(0,0,0,0.08)",
        }}
      >
        <div
          style={{
            width: "38px",
            height: "38px",
            borderRadius: "50%",
            background:
              "linear-gradient(135deg,#2563eb,#7c3aed)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            fontWeight: "800",
            fontSize: "1rem",
          }}
        >
          {user?.username
            ?.charAt(0)
            ?.toUpperCase()}
        </div>

        <span
          style={{
            fontWeight: "700",
            color: "#1e293b",
            maxWidth: "120px",
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
          }}
        >
          {user.username}
        </span>
      </motion.button>

      <AnimatePresence>
        {isDropdownOpen && (
          <motion.div
            initial={{
              opacity: 0,
              y: 10,
              scale: 0.95,
            }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
            }}
            exit={{
              opacity: 0,
              y: 10,
              scale: 0.95,
            }}
            transition={{
              duration: 0.2,
            }}
            style={{
              position: "absolute",
              top: "120%",
              right: 0,
              width: "280px",
              background: "white",
              borderRadius: "24px",
              overflow: "hidden",
              boxShadow:
                "0 20px 50px rgba(0,0,0,0.15)",
              border: "1px solid #e2e8f0",
              zIndex: 1000,
            }}
          >
            {/* TOP */}

            <div
              style={{
                padding: "22px",
                background:
                  "linear-gradient(135deg,#2563eb,#7c3aed)",
                color: "white",
              }}
            >
              <div
                style={{
                  fontWeight: "800",
                  fontSize: "1.1rem",
                }}
              >
                {user.username}
              </div>

              <div
                style={{
                  fontSize: "0.85rem",
                  opacity: 0.9,
                  marginTop: "4px",
                }}
              >
                {user.email || "No Email"}
              </div>
            </div>

            {/* MENU */}

            <div
              style={{
                padding: "12px",
              }}
            >
              {/* PROFILE */}

              {(isUser || isDoctor) && (
                <Link
                  to="/up"
                  style={dropdownItemStyle}
                  onClick={() =>
                    setIsDropdownOpen(false)
                  }
                >
                  <FaUser />
                  My Profile
                </Link>
              )}

              {/* DOCTOR PROFILE */}

              {isDoctor && (
                <Link
                  to="/df"
                  style={dropdownItemStyle}
                  onClick={() =>
                    setIsDropdownOpen(false)
                  }
                >
                  <FaCheckCircle />
                  Complete Profile
                </Link>
              )}

              {/* ORDERS */}

              <Link
                to="/uoo"
                style={dropdownItemStyle}
                onClick={() =>
                  setIsDropdownOpen(false)
                }
              >
                <FaClipboardList />
                My Orders
              </Link>

              {/* CART */}

              <button
                onClick={() => {
                  setIsDropdownOpen(false);
                  handleCartClick();
                }}
                style={dropdownButtonStyle}
              >
                <FaShoppingCart />
                Shopping Cart
              </button>

              {/* LOGOUT */}

              <button
                onClick={(e) => {
                  setIsDropdownOpen(false);
                  performLogout(e);
                }}
                style={{
                  ...dropdownButtonStyle,
                  color: "#ef4444",
                }}
              >
                <FaSignOutAlt />
                Logout
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// =====================================================
// MOBILE SIDEBAR
// =====================================================

const MobileSidebar = ({
  user,
  isSidebarOpen,
  setIsSidebarOpen,
  performLogout,
  handleCartClick,
}) => {
  if (!isSidebarOpen) return null;

  const isDoctor =
    user?.roles?.some(
      (role) => role.name === "DOCTOR"
    );

  const isUser =
    user?.roles?.some(
      (role) => role.name === "USER"
    );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={() => setIsSidebarOpen(false)}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 2000,
        backdropFilter: "blur(5px)",
      }}
    >
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{
          type: "spring",
          damping: 20,
        }}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "absolute",
          right: 0,
          top: 0,
          width: "85%",
          maxWidth: "340px",
          height: "100%",
          background: "white",
          padding: "20px",
          overflowY: "auto",
        }}
      >
        {/* HEADER */}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "25px",
          }}
        >
          <h2
            style={{
              margin: 0,
              color: "#1e293b",
            }}
          >
            Menu
          </h2>

          <button
            onClick={() =>
              setIsSidebarOpen(false)
            }
            style={{
              border: "none",
              background: "#f1f5f9",
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              cursor: "pointer",
            }}
          >
            <FaTimes />
          </button>
        </div>

        {/* USER */}

        {user?.username && (
          <div
            style={{
              padding: "20px",
              borderRadius: "24px",
              background:
                "linear-gradient(135deg,#2563eb,#7c3aed)",
              color: "white",
              marginBottom: "25px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "14px",
              }}
            >
              <div
                style={{
                  width: "55px",
                  height: "55px",
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "800",
                  fontSize: "1.3rem",
                }}
              >
                {user.username
                  ?.charAt(0)
                  ?.toUpperCase()}
              </div>

              <div>
                <div
                  style={{
                    fontWeight: "800",
                    fontSize: "1rem",
                  }}
                >
                  {user.username}
                </div>

                <div
                  style={{
                    fontSize: "0.8rem",
                    opacity: 0.9,
                  }}
                >
                  {user.email}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* NAVIGATION */}

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          <Link
            to="/"
            style={mobileLinkStyle}
            onClick={() =>
              setIsSidebarOpen(false)
            }
          >
            <FaHome />
            Home
          </Link>

          <Link
            to="/mp"
            style={mobileLinkStyle}
            onClick={() =>
              setIsSidebarOpen(false)
            }
          >
            <FaPills />
            Medicine
          </Link>

          <Link
            to="/dc"
            style={mobileLinkStyle}
            onClick={() =>
              setIsSidebarOpen(false)
            }
          >
            <FaPhone />
            Doctor
          </Link>

          <Link
            to="/ltp"
            style={mobileLinkStyle}
            onClick={() =>
              setIsSidebarOpen(false)
            }
          >
            <FaFlask />
            Lab Test
          </Link>

          {/* PROFILE */}

          {(isUser || isDoctor) && (
            <Link
              to="/up"
              style={mobileLinkStyle}
              onClick={() =>
                setIsSidebarOpen(false)
              }
            >
              <FaUser />
              My Profile
            </Link>
          )}

          {/* ORDERS */}

          <Link
            to="/uoo"
            style={mobileLinkStyle}
            onClick={() =>
              setIsSidebarOpen(false)
            }
          >
            <FaBox />
            My Orders
          </Link>

          {/* DOCTOR */}

          {isDoctor && (
            <Link
              to="/df"
              style={mobileLinkStyle}
              onClick={() =>
                setIsSidebarOpen(false)
              }
            >
              <FaCheckCircle />
              Complete Profile
            </Link>
          )}

          {/* CART */}

          <button
            onClick={() => {
              setIsSidebarOpen(false);
              handleCartClick();
            }}
            style={mobileButtonStyle}
          >
            <FaShoppingCart />
            Shopping Cart
          </button>

          {/* LOGOUT */}

          {user?.username ? (
            <button
              onClick={(e) => {
                setIsSidebarOpen(false);
                performLogout(e);
              }}
              style={{
                ...mobileButtonStyle,
                color: "#ef4444",
              }}
            >
              <FaSignOutAlt />
              Logout
            </button>
          ) : (
            <>
              <Link
                to="/login"
                style={mobileLinkStyle}
                onClick={() =>
                  setIsSidebarOpen(false)
                }
              >
                <FaUser />
                Login
              </Link>

              <Link
                to="/su"
                style={mobileLinkStyle}
                onClick={() =>
                  setIsSidebarOpen(false)
                }
              >
                <FaUser />
                Signup
              </Link>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// =====================================================
// MAIN NAVBAR
// =====================================================

const Navbar = () => {
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem("user");

    return savedUser
      ? JSON.parse(savedUser)
      : null;
  });

  const [showCartSidebar, setShowCartSidebar] =
    useState(false);

  const [cartItems, setCartItems] = useState([]);

  const [isSidebarOpen, setIsSidebarOpen] =
    useState(false);

  const [isScrolled, setIsScrolled] =
    useState(false);

  const location = useLocation();

  const activeLink = location.pathname;

  const isMobileScreen = useScreenSize(768);

  // =====================================================

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);

    return () =>
      window.removeEventListener(
        "scroll",
        handleScroll
      );
  }, []);

  // =====================================================

  const handleCartClick = useCallback(() => {
    setShowCartSidebar((prev) => !prev);
  }, []);

  // =====================================================

  const performLogout = async (e) => {
    e?.preventDefault();

    try {
      await fetch(`${BASE_URL}/logout`, {
        method: "GET",
        credentials: "include",
      });
    } catch (err) {
      console.error(err);
    }

    localStorage.removeItem("user");

    setUser(null);

    window.location.href = "/";
  };

  // =====================================================

  useEffect(() => {
    if (showCartSidebar) {
      fetch(
        `${BASE_URL}/user/getAllCartProduct`,
        {
          credentials: "include",
        }
      )
        .then((res) => res.json())
        .then((data) => {
          setCartItems(
            Array.isArray(data)
              ? data
              : data.body || []
          );
        })
        .catch((err) => console.error(err));
    }
  }, [showCartSidebar]);

  // =====================================================

  const deleteFromCart = async (cartId) => {
    try {
      const response = await fetch(
        `${BASE_URL}/user/deleteCartProduct/${cartId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (response.ok) {
        setCartItems((prev) =>
          prev.filter((item) => item.id !== cartId)
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      {/* NAVBAR */}

      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          height: navbarHeight,
          background: isScrolled
            ? "rgba(255,255,255,0.85)"
            : "white",
          backdropFilter: "blur(14px)",
          borderBottom: "1px solid #e2e8f0",
          boxShadow: isScrolled
            ? "0 10px 30px rgba(0,0,0,0.08)"
            : "none",
        }}
      >
        <div
          style={{
            maxWidth: "1400px",
            margin: "0 auto",
            height: "100%",
            padding: "0 18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* LOGO */}

          <Link
            to="/"
            style={{
              textDecoration: "none",
            }}
          >
            <AnimatedLogo
              isMobileScreen={isMobileScreen}
            />
          </Link>

          {/* DESKTOP LINKS */}

          {!isMobileScreen && (
            <div
              style={{
                display: "flex",
                gap: "8px",
              }}
            >
              <DesktopNavLink
                to="/"
                icon={FaHome}
                active={activeLink === "/"}
              >
                Home
              </DesktopNavLink>

              <DesktopNavLink
                to="/mp"
                icon={FaPills}
                active={activeLink === "/mp"}
              >
                Medicine
              </DesktopNavLink>

              <DesktopNavLink
                to="/dc"
                icon={FaPhone}
                active={activeLink === "/dc"}
              >
                Doctor
              </DesktopNavLink>

              <DesktopNavLink
                to="/ltp"
                icon={FaFlask}
                active={activeLink === "/ltp"}
              >
                Lab Test
              </DesktopNavLink>
            </div>
          )}

          {/* RIGHT */}

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            {/* CART */}

            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={handleCartClick}
              style={cartBtnStyle}
            >
              <FaShoppingCart size={18} />

              {cartItems.length > 0 && (
                <span style={badgeStyle}>
                  {cartItems.length}
                </span>
              )}
            </motion.button>

            {/* MOBILE */}

            {isMobileScreen ? (
              <button
                onClick={() =>
                  setIsSidebarOpen(true)
                }
                style={{
                  border: "none",
                  background: "#f1f5f9",
                  width: "45px",
                  height: "45px",
                  borderRadius: "14px",
                  cursor: "pointer",
                  fontSize: "1.2rem",
                }}
              >
                <FaBars />
              </button>
            ) : user?.username ? (
              <UserProfileDropdown
                user={user}
                handleCartClick={handleCartClick}
                performLogout={performLogout}
              />
            ) : (
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                }}
              >
                <Link
                  to="/login"
                  style={loginBtnStyle}
                >
                  Login
                </Link>

                <Link
                  to="/su"
                  style={signupBtnStyle}
                >
                  Signup
                </Link>
              </div>
            )}
          </div>
        </div>
      </motion.nav>

      {/* MOBILE SIDEBAR */}

      <AnimatePresence>
        {isSidebarOpen && (
          <MobileSidebar
            user={user}
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={setIsSidebarOpen}
            performLogout={performLogout}
            handleCartClick={handleCartClick}
          />
        )}
      </AnimatePresence>

      {/* CART SIDEBAR */}

      <AnimatePresence>
        {showCartSidebar && (
          <CartSidebar
            cartItems={cartItems}
            onClose={() =>
              setShowCartSidebar(false)
            }
            deleteFromCart={deleteFromCart}
          />
        )}
      </AnimatePresence>
    </>
  );
};

// =====================================================
// HELPER COMPONENTS
// =====================================================

const DesktopNavLink = ({
  to,
  icon: Icon,
  children,
  active,
}) => {
  return (
    <Link
      to={to}
      style={{
        textDecoration: "none",
      }}
    >
      <motion.div
        whileHover={{ y: -2 }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "11px 18px",
          borderRadius: "40px",
          fontWeight: "700",
          fontSize: "0.92rem",
          background: active
            ? "linear-gradient(135deg,#2563eb,#7c3aed)"
            : "transparent",
          color: active ? "white" : "#475569",
        }}
      >
        <Icon size={15} />
        {children}
      </motion.div>
    </Link>
  );
};

// =====================================================
// STYLES
// =====================================================

const dropdownItemStyle = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "14px 16px",
  textDecoration: "none",
  color: "#334155",
  borderRadius: "14px",
  fontWeight: "600",
  marginBottom: "4px",
};

const dropdownButtonStyle = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "14px 16px",
  border: "none",
  background: "transparent",
  borderRadius: "14px",
  cursor: "pointer",
  color: "#334155",
  fontWeight: "600",
  fontSize: "0.95rem",
};

const mobileLinkStyle = {
  display: "flex",
  alignItems: "center",
  gap: "14px",
  padding: "15px",
  borderRadius: "18px",
  textDecoration: "none",
  background: "#f8fafc",
  color: "#334155",
  fontWeight: "700",
};

const mobileButtonStyle = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: "14px",
  padding: "15px",
  borderRadius: "18px",
  border: "none",
  background: "#f8fafc",
  color: "#334155",
  fontWeight: "700",
  fontSize: "0.95rem",
  cursor: "pointer",
};

const cartBtnStyle = {
  border: "none",
  width: "45px",
  height: "45px",
  borderRadius: "50%",
  background: "#f1f5f9",
  cursor: "pointer",
  position: "relative",
  color: "#1e293b",
};

const badgeStyle = {
  position: "absolute",
  top: "-4px",
  right: "-4px",
  width: "18px",
  height: "18px",
  borderRadius: "50%",
  background: "#ef4444",
  color: "white",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "0.7rem",
  fontWeight: "800",
};

const loginBtnStyle = {
  textDecoration: "none",
  padding: "11px 22px",
  borderRadius: "40px",
  border: "2px solid #2563eb",
  color: "#2563eb",
  fontWeight: "700",
};

const signupBtnStyle = {
  textDecoration: "none",
  padding: "11px 22px",
  borderRadius: "40px",
  background:
    "linear-gradient(135deg,#2563eb,#7c3aed)",
  color: "white",
  fontWeight: "700",
  boxShadow: "0 10px 20px rgba(37,99,235,0.25)",
};

export default Navbar;